/**
 * Cleanup Manager
 *
 * Manages audit event cleanup and retention policies
 */

import { AuditEvent, AuditLevel } from './types';
import { configureForEnvironment, type EnvironmentLevel } from './utility-functions';

export class CleanupManager {
  private maxEvents: number;
  private retentionDays: number;
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor(auditLevel: AuditLevel) {
    // Convert AuditLevel to EnvironmentLevel for configuration
    const environmentLevel = this.mapAuditLevelToEnvironmentLevel(auditLevel);
    const config = configureForEnvironment(environmentLevel);
    this.maxEvents = config.maxEvents;
    this.retentionDays = config.retentionDays;
    this.initializeCleanup();
  }

  /**
   * Initialize periodic cleanup
   */
  private initializeCleanup(): void {
    // Run cleanup every hour
    this.cleanupTimer = setInterval((_interval) => {
      // This will be called by the main logger with its events array
    }, 60 * 60 * 1000) as unknown as NodeJS.Timeout;
  }

  /**
   * Perform cleanup on event array
   */
  cleanup(events: AuditEvent[]): AuditEvent[] {
    let cleanedEvents = [...events];

    // Limit by maximum event count
    if (cleanedEvents.length > this.maxEvents) {
      // Keep only the most recent events
      cleanedEvents = cleanedEvents
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, this.maxEvents);
    }

    // Filter by retention period
    const cutoffTime = Date.now() - (this.retentionDays * 24 * 60 * 60 * 1000);
    cleanedEvents = cleanedEvents.filter(event =>
      new Date(event.timestamp).getTime() >= cutoffTime
    );

    return cleanedEvents;
  }

  /**
   * Check if cleanup should be triggered
   */
  shouldTriggerCleanup(eventCount: number): boolean {
    return eventCount > this.maxEvents * 0.9;
  }

  /**
   * Map AuditLevel to EnvironmentLevel for configuration
   */
  private mapAuditLevelToEnvironmentLevel(auditLevel: AuditLevel): EnvironmentLevel {
    switch (auditLevel) {
      case 'INFO':
        return 'DEVELOPMENT';
      case 'WARN':
        return 'STAGING';
      case 'ERROR':
        return 'PRODUCTION';
      case 'CRITICAL':
        return 'ENTERPRISE';
      default:
        return 'DEVELOPMENT';
    }
  }

  /**
   * Update configuration
   */
  updateConfig(auditLevel: AuditLevel): void {
    const environmentLevel = this.mapAuditLevelToEnvironmentLevel(auditLevel);
    const config = configureForEnvironment(environmentLevel);
    this.maxEvents = config.maxEvents;
    this.retentionDays = config.retentionDays;
  }

  /**
   * Stop cleanup timer
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): { maxEvents: number; retentionDays: number } {
    return {
      maxEvents: this.maxEvents,
      retentionDays: this.retentionDays
    };
  }
}